<template>
    <div :class="switchWrapperMergedClasses" ref="switchWrapper">
        <!-- Dynamic Selection Indicator -->
        <div
            ref="selectionIndicator"
            class="bg-blue-500 rounded-full absolute top-1 bottom-1 left-0 transition-all pointer-events-none ease-in-out"
            :style="selectionIndicatorStyle"
            :class="[selectionIndicatorCanTransition ? 'duration-300' : 'duration-0']"
        ></div>

        <!-- Options -->
        <Button
            v-for="(option, index) in options"
            :key="index"
            @click="selectOption(index)"
            :label="option.label"
            :icon="option.icon"
            :icon-filled="option.iconFilled"
            :size="size"
            variant="transparent"
            data-ref="option"
            :class="buttonClasses"
        />
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, nextTick, watch } from 'vue'
    import { twMerge } from 'tailwind-merge'
    import Button from '../buttons/Button.vue'
    import { IconType } from '../icons'

    const selectedIndex = defineModel<boolean>({ default: () => false, required: true })

    const props = withDefaults(
        defineProps<{
            class?: string
            options: [
                {
                    label?: string
                    icon?: IconType
                    iconFilled?: boolean
                },
                {
                    label?: string
                    icon?: IconType
                    iconFilled?: boolean
                }
            ]
            size?: 'sm' | 'md' | 'lg'
            variant?: 'light' | 'neutral'
        }>(),
        {
            size: 'md',
            variant: 'light'
        }
    )

    const selectionIndicator = ref<HTMLElement | null>(null) // Reference to the indicator element
    const switchWrapper = ref<HTMLElement | null>(null) // Reference to the indicator element

    const selectionIndicatorCanTransition = ref(true)
    const selectionIndicatorStyle = ref({
        width: `0px`,
        transform: `translateX(0px)`
    })

    const selectOption = (index: number) => {
        selectedIndex.value = index === 1 ? true : false
        adjustSelectionIndicator()
    }

    const adjustSelectionIndicator = async () => {
        if (switchWrapper.value) {
            // Ensure DOM updates are completed
            await nextTick()

            // Somehow array refs on child components don't work. Using plain js instead.
            const button = switchWrapper.value.querySelectorAll('[data-ref="option"]')[selectedIndex.value ? 1 : 0] as HTMLElement

            if (button && selectionIndicator.value) {
                selectionIndicatorStyle.value = {
                    width: `${button.offsetWidth}px`,
                    transform: `translateX(${button.offsetLeft}px)`
                }
            }
        }
    }

    onMounted(() => {
        initializeResizeObserver()
    })

    const initializeResizeObserver = () => {
        const resizeObserver = new ResizeObserver(async (entries) => {
            selectionIndicatorCanTransition.value = false
            adjustSelectionIndicator()
            // Ensure resize happened before enabling transitions
            setTimeout(() => {
                selectionIndicatorCanTransition.value = true
            }, 0)
        })
        resizeObserver.observe(switchWrapper.value!)
    }

    watch(() => selectedIndex.value, adjustSelectionIndicator)

    const buttonClasses = computed(() => {
        const sizeOptions = {
            sm: 'min-w-9',
            md: 'min-w-11',
            lg: 'min-w-14'
        }

        return ['relative z-1 truncate', sizeOptions[props.size]]
    })

    const switchWrapperBaseClasses = computed(() => {
        const variantOptions = {
            light: 'bg-system-light',
            neutral: 'bg-surface-background'
        }
        const sizeOptions = {
            sm: 'h-10',
            md: 'h-12',
            lg: 'h-14'
        }

        return ['relative grid grid-cols-2 auto-cols-fr whitespace-nowrap inline-flex rounded-full p-1 shadow-sm', variantOptions[props.variant], sizeOptions[props.size]]
    })

    // Allow overriding base classes
    const switchWrapperMergedClasses = computed(() => twMerge(switchWrapperBaseClasses.value, props.class))
</script>
